import { Injectable, inject } from '@angular/core';
import { CanActivateChild, Router, ActivatedRouteSnapshot, RouterStateSnapshot, UrlTree } from '@angular/router';
import { Observable, from, of } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { PermissionService } from '@shared/permission-service.ts/resorces-permission.service';

/**
 * Route used as the fallback when a user is denied any other route.
 * The guard must NEVER redirect to this route when it is itself denied —
 * doing so would create an infinite navigation loop.
 */
const FALLBACK_ROUTE = '/dashboard';

/**
 * MenuPermissionGuard — blocks authenticated users from directly navigating
 * to routes their permissions don't allow.
 *
 * Uses the same IndexedDB-backed PermissionService data the sidebar uses to
 * show/hide links, so the two are always in sync.
 *
 * Behaviour:
 *   • Route matches a MENUS entry and user CAN view it   → allow
 *   • Route matches a MENUS entry and user CANNOT view it → redirect to /dashboard
 *   • Route has no matching MENUS entry (dev/utility)    → allow (fail-open)
 *   • PermissionService throws any error                 → allow (fail-open)
 */
@Injectable({
  providedIn: 'root',
})
export class MenuPermissionGuard implements CanActivateChild {
  private readonly permissionService = inject(PermissionService);
  private readonly router = inject(Router);

  canActivateChild(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean | UrlTree> {
    // Read directly from IndexedDB — no API call, never blocks navigation.
    // The sidebar populates IndexedDB via updatePermission() on app init.
    // If IDB is empty (first navigation after login, sidebar hasn't loaded yet)
    // the guard fails-open below when menusCategory is not found.
    return from(this.permissionService.getAllResourceCategories()).pipe(
      switchMap((categories: any[]) => {
        const menusCategory = categories.find((c: any) => c.Name === 'MENUS');

        if (!menusCategory) {
          // Permissions data not ready yet — fail-open
          return of(true);
        }

        const allMenuItems: any[] = this.flattenMenuItems(
          menusCategory?.resource_types?.[0]?.resources ?? []
        );

        // Strip query string and trailing slash for clean matching
        const currentPath = state.url.split('?')[0].replace(/\/$/, '') || '/';

        // Find the best (longest) matching menu item
        const matchedItem = this.findBestMatch(allMenuItems, currentPath);

        if (!matchedItem) {
          // No menu entry for this route — allow through (fail-open)
          return of(true);
        }

        const effective = this.permissionService.getEffectivePermission(
          matchedItem.resource_permission
        );

        const canView =
          effective &&
          effective.CanView === 1

        if (canView) {
          return of(true);
        }

        // ── Infinite-loop prevention ────────────────────────────────────────
        // If the denied route IS the fallback redirect target, failing open is
        // safer than looping. This can happen when:
        //   • Permissions haven't been fetched yet (race on first load)
        //   • The current user has no dashboard-view permission at all
        //   • IDB holds stale data from a previous user session
        if (currentPath === FALLBACK_ROUTE) {
          console.warn(
            `[MenuPermissionGuard] Access denied to fallback route "${FALLBACK_ROUTE}" — failing open to prevent infinite redirect loop.`
          );
          return of(true);
        }

        console.warn(
          `[MenuPermissionGuard] Access denied to "${currentPath}". Redirecting to ${FALLBACK_ROUTE}.`
        );
        return of(this.router.createUrlTree([FALLBACK_ROUTE]));
      }),
      catchError((err) => {
        console.error('[MenuPermissionGuard] Error checking permissions, allowing navigation:', err);
        return of(true);
      })
    );
  }

  /**
   * Flattens parent menu items and their children into a single array.
   * Only includes items that have a UrlRoute.
   */
  private flattenMenuItems(items: any[]): any[] {
    const result: any[] = [];
    for (const item of items) {
      if (item.UrlRoute) {
        result.push(item);
      }
      if (item.children?.length) {
        for (const child of item.children) {
          if (child.UrlRoute) {
            result.push(child);
          }
        }
      }
    }
    return result;
  }

  /**
   * Returns the menu item whose UrlRoute is the longest prefix matching
   * the current path (most-specific match wins).
   */
  private findBestMatch(items: any[], currentPath: string): any | null {
    let best: any | null = null;
    let bestLen = 0;

    for (const item of items) {
      const route: string = item.UrlRoute;
      if (!route) continue;

      const normalised = route.replace(/\/$/, '');
      const isMatch =
        currentPath === normalised ||
        currentPath.startsWith(normalised + '/');

      if (isMatch && normalised.length > bestLen) {
        best = item;
        bestLen = normalised.length;
      }
    }

    return best;
  }
}
